import React from 'react';
import { ImageSizer, ImageUris } from 'js/helpers/ImageSizer';
import debounce from 'lodash.debounce';
import styles from './ResizingImage.module.css';

interface Props {
  imageUris: ImageUris;
  upscaleAllowance?: number;
  /**
   * Whether the aspect ration of the image will be preserved.
   * The default is true.
   */
  preserveAspectRatio?: boolean;
  alt?: string;
}

interface ImageDimensions {
  width: number;
  height: number;
}

interface State {
  url?: string;
  isMounted: boolean;
  imageDimensions: ImageDimensions;
}

/*
 * This a React component integrating with ImageSizer. It renders an element with an image
 * which is currently an img tag.
 *
 * It passes the element's width to ImageSizer to choose the most appropriate image resolution url.
 * It listens to the browser resize event as this could change the element's width.
 *
 * If initialWidth is provided, use this to choose a url prior to the element being mounted.
 * If it is not set no image will be shown until it has mounted, this will primarily impact
 * server-side rendered HTML as is intended. InitialWidth should typically be estimation of
 * what the width will be on a mobile device.
 */
export class ResizingImage extends React.PureComponent<Props, State> {
  private imageSizer: ImageSizer;

  private preloadingImage?: HTMLImageElement;

  public imageContainerReference: React.RefObject<HTMLDivElement | null>;

  public static defaultProps = {
    preserveAspectRatio: true,
  };

  public constructor(props: Props) {
    super(props);

    this.imageSizer = new ImageSizer(props.imageUris);
    this.imageContainerReference = React.createRef<HTMLDivElement>();

    const url = this.imageSizer.getBestImage(500, false);

    this.state = {
      url,
      isMounted: false,
      imageDimensions: this.getImageDimensions(url),
    };
  }

  public componentDidMount(): void {
    window.addEventListener('resize', this.onResize);
    this.updateSize();
    this.setState({ isMounted: true });
  }

  public componentWillUnmount(): void {
    window.removeEventListener('resize', this.onResize);
  }

  public componentDidUpdate(prevProps: Props): void {
    if (this.props.imageUris !== prevProps.imageUris) {
      if (this.preloadingImage) {
        this.preloadingImage.onload = null;
      }

      this.imageSizer = new ImageSizer(this.props.imageUris);
      const url = this.getBestImage();

      if (!url) {
        return;
      }

      this.setState({ url });
    }
  }

  private getBestImage(): string | null {
    // This should not occur but handles if the element is not (or no longer) mounted
    if (!this.imageContainerReference.current) {
      return null;
    }

    return this.imageSizer.getBestImage(
      this.imageContainerReference.current.offsetWidth,
      true,
      this.props.upscaleAllowance
    );
  }

  private loadImage(imageUrl: string): void {
    // Preload and cache the image before overriding to avoid no image state
    if (this.preloadingImage) {
      this.preloadingImage.onload = null;
    }

    this.preloadingImage = document.createElement('img');
    this.preloadingImage.src = imageUrl;
    this.setState({
      imageDimensions: this.getImageDimensions(imageUrl),
      url: imageUrl,
    });
  }

  private updateSize = (): void => {
    const url = this.getBestImage();

    if (!url || url === this.state.url) {
      return;
    }

    this.loadImage(url);
  };

  private getImageDimensions(imageUrl: string): ImageDimensions {
    const dimensions = { width: 0, height: 0 };

    const dimensionString: string | undefined = Object.keys(
      this.props.imageUris
    ).find((dimension) => this.props.imageUris[dimension] === imageUrl);

    if (dimensionString) {
      const imageSize: string[] = dimensionString.split('x');
      dimensions.width = Number(imageSize[0]);
      dimensions.height = Number(imageSize[1]);
    }

    return dimensions;
  }

  private onResize = debounce(this.updateSize, 100);

  public render(): React.ReactNode {
    const { imageDimensions } = this.state;

    let style: React.CSSProperties | undefined;
    if (this.props.preserveAspectRatio) {
      const padding = (imageDimensions.height / imageDimensions.width) * 100;
      style = { paddingTop: `${padding}%` };
    }

    const img = (
      <img
        src={this.state.url}
        className={styles.image}
        alt={this.props.alt ?? ''}
      />
    );

    return (
      <div
        className={styles.imageContainer}
        style={style}
        ref={this.imageContainerReference}
      >
        {this.state.isMounted ? img : <noscript>{img}</noscript>}
      </div>
    );
  }
}
